import Button from '@orn/shared-web/components/Button/Button';
import { HookForm, FormField } from '@orn/shared-web/components/Form';
import Input from '@orn/shared-web/components/Input';
import { PasswordChecklist, PasswordInput } from '@orn/shared-web/components/Input';
import validatePassword from '@orn/shared-web/lib/validation/passwordValidation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import useAuthApi from '../../../hooks/useAuthApi';
import LoginHeader from '../LoginHeader';
import LoginTitle from '../LoginTitle';

interface IRegistrationStepProps {
  email: string;
  onNext: (result: { userId: string }) => void;
  onBack: () => void;
}

interface IRegistrationFormValues {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

const RegistrationStep = ({ email, onNext, onBack }: IRegistrationStepProps) => {
  const authApi = useAuthApi();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [serverPasswordError, setServerPasswordError] = React.useState<string | undefined>();
  const form = useForm<IRegistrationFormValues>({
    defaultValues: { firstName: '', lastName: '', password: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const firstNameValue = form.watch('firstName');
  const lastNameValue = form.watch('lastName');
  const passwordValue = form.watch('password');
  const confirmValue = form.watch('confirmPassword');

  const handleSubmit = React.useCallback(
    async (values: IRegistrationFormValues) => {
      setServerPasswordError(undefined);

      // Client-side password checks
      const { rules, maxLengthError } = validatePassword(values.password);
      if (!rules.every((r) => r.met) || maxLengthError) {
        return;
      }

      if (values.password !== values.confirmPassword) {
        form.setError('confirmPassword', { message: 'Passwords do not match.' });
        return;
      }

      setIsSubmitting(true);
      const result = await authApi.register({
        email,
        password: values.password,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
      });
      setIsSubmitting(false);

      if (!result.ok) {
        if (result.error.code === 'AUTH_WEAK_PASSWORD') {
          setServerPasswordError(result.error.error);
        } else if (result.error.code === 'AUTH_PROVIDER_CONFLICT') {
          form.setError('firstName', {
            message: 'This email uses Google sign-in. Please go back and use the Google button.',
          });
        } else if (result.error.code === 'AUTH_EMAIL_ALREADY_REGISTERED') {
          form.setError('firstName', { message: 'This email is already registered. Please sign in instead.' });
        } else if (result.error.code === 'AUTH_INVALID_FIRST_NAME') {
          form.setError('firstName', { message: result.error.error });
        } else if (result.error.code === 'AUTH_INVALID_LAST_NAME') {
          form.setError('lastName', { message: result.error.error });
        } else {
          form.setError('firstName', { message: result.error.error });
        }
        return;
      }

      onNext({ userId: result.data.userId });
    },
    [authApi, email, form, onNext]
  );

  const passwordValidation = validatePassword(passwordValue);
  const passwordsMatch = passwordValue.length > 0 && confirmValue.length > 0 && passwordValue === confirmValue;
  const isPasswordValid = passwordValidation.rules.every((r) => r.met) && !passwordValidation.maxLengthError;
  const isFormFilled =
    firstNameValue.trim() !== '' && lastNameValue.trim() !== '' && passwordValue !== '' && confirmValue !== '';

  return (
    <>
      <LoginHeader onBack={onBack} />
      <LoginTitle title="Create your account" />

      <HookForm form={form} onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="flex w-full flex-col gap-4">
          {/* Email (read-only display) */}
          <div className="flex flex-col gap-1.5">
            <span className="text-primary text-sm font-medium">Email</span>
            <span className="text-secondary text-sm">{email}</span>
          </div>

          <div className="flex gap-4">
            <FormField control={form.control} name="firstName">
              {({ field, fieldState }) => (
                <Input
                  {...field}
                  autoFocus
                  label="First name"
                  placeholder="First name"
                  size="md"
                  isRequired
                  isInvalid={Boolean(fieldState.error)}
                  hint={fieldState.error?.message}
                  autoComplete="given-name"
                />
              )}
            </FormField>

            <FormField control={form.control} name="lastName">
              {({ field, fieldState }) => (
                <Input
                  {...field}
                  label="Last name"
                  placeholder="Last name"
                  size="md"
                  isRequired
                  isInvalid={Boolean(fieldState.error)}
                  hint={fieldState.error?.message}
                  autoComplete="family-name"
                />
              )}
            </FormField>
          </div>

          <FormField control={form.control} name="password">
            {({ field }) => (
              <PasswordInput {...field} label="Password" size="md" isRequired autoComplete="new-password" />
            )}
          </FormField>

          <FormField control={form.control} name="confirmPassword">
            {({ field, fieldState }) => (
              <PasswordInput
                {...field}
                label="Confirm password"
                size="md"
                isRequired
                isInvalid={Boolean(fieldState.error)}
                hint={fieldState.error?.message}
                autoComplete="new-password"
              />
            )}
          </FormField>

          {/* Password checklist below both password fields */}
          <PasswordChecklist
            password={passwordValue}
            confirmPassword={confirmValue}
            serverError={serverPasswordError}
          />

          <Button
            type="submit"
            size="xl"
            color="primary"
            className="w-full"
            isDisabled={!isFormFilled || !isPasswordValid || !passwordsMatch}
            isLoading={isSubmitting}
          >
            Create account
          </Button>
        </div>
      </HookForm>
    </>
  );
};

RegistrationStep.displayName = 'RegistrationStep';

export default RegistrationStep;
